import { useEffect, useState, useCallback, useRef, memo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Stack,
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/material";

const toTitleCase = (str = "") =>
  String(str)
    .replace(/[_\-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const deriveLabel = (field) =>
  field.label || toTitleCase(field.name) || field.name;

const getDemoValue = (field) => {
  const key = (field.name || "").toLowerCase();
  const lbl = deriveLabel(field).toLowerCase();
  const combined = key + " " + lbl;

  if (/amount|cost|fee|price|total|subtotal|value/.test(combined))
    return "$1,500.00";

  if (/date|expir|issued|start|end/.test(combined)) return "01/15/2025";

  if (/phone|mobile|fax/.test(combined)) return "555-867-5309";

  if (/email/.test(combined)) return "sample@example.com";

  if (/zip|postal/.test(combined)) return "33101";

  if (/\bstate\b/.test(combined)) return "FL";

  if (/\bcity\b/.test(combined)) return "Miami";

  if (/county/.test(combined)) return "Miami-Dade";

  if (/address|street|addr/.test(combined)) return "123 Main Street";

  if (/unit|apt|suite|ste/.test(combined)) return "Suite 100";

  if (/owner|applicant|signer|qualifier|signee/.test(combined))
    return "John A. Smith";
  if (/contractor|company|business|corp|firm/.test(combined))
    return "ABC Construction LLC";
  if (/\bname\b/.test(combined)) return "Jane Doe";

  if (/license|permit|application|num|number/.test(combined))
    return "LIC-2025-001";

  if (/desc|note|comment|work|scope|detail/.test(combined))
    return "Sample description of work to be performed.";

  if (/sq|area|footage|sqft/.test(combined)) return "1,200";

  if (/project|package|lot|block/.test(combined)) return "Sample Project A";

  if (/folio|parcel/.test(combined)) return "01-2345-678-9000";

  return deriveLabel(field) || "Sample Text";
};

const LocalTextField = memo(({ field, onCommit, demoVersion }) => {
  const label = deriveLabel(field);
  const [localValue, setLocalValue] = useState(field.value || "");
  const prevDemoRef = useRef(demoVersion);

  useEffect(() => {
    if (demoVersion !== prevDemoRef.current || field.value !== localValue) {
      setLocalValue(field.value || "");
      prevDemoRef.current = demoVersion;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, demoVersion]);

  const isLong = typeof localValue === "string" && localValue.length > 80;
  const isDollar =
    field.name?.toLowerCase().includes("amount") ||
    field.name?.toLowerCase().includes("cost") ||
    field.name?.toLowerCase().includes("fee");

  return (
    <TextField
      variant="filled"
      fullWidth
      margin="dense"
      label={label}
      value={localValue}
      multiline={isLong}
      minRows={isLong ? 2 : undefined}
      maxRows={isLong ? 5 : undefined}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(field, localValue)}
      slotProps={{
        input: {
          startAdornment:
            isDollar && localValue ? (
              <InputAdornment position="start">$</InputAdornment>
            ) : null,
        },
      }}
    />
  );
});

const InteractiveField = memo(({ field, onChange, demoVersion }) => {
  const label = deriveLabel(field);
  const value = field.value;

  if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

  if (field.type === "PDFCheckBox") {
    return (
      <Box sx={{ py: 0.5, px: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(field, e.target.checked)}
            />
          }
          label={<Typography variant="body2">{label}</Typography>}
        />
      </Box>
    );
  }

  if (field.type === "PDFRadioGroup") {
    return (
      <Box sx={{ py: 0.5, px: 0.5 }}>
        <RadioGroup
          row
          value={value ? "yes" : ""}
          onChange={(e) => onChange(field, e.target.value === "yes")}
        >
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label={<Typography variant="body2">{label}</Typography>}
          />
        </RadioGroup>
      </Box>
    );
  }

  return (
    <LocalTextField
      field={field}
      onCommit={onChange}
      demoVersion={demoVersion}
    />
  );
});

const SectionCard = memo(({ item, onFieldChange, demoVersion }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      border: "2px solid #d3d3d3",
      borderRadius: "15px",
      px: 2,
      pb: 1,
      boxShadow:
        "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
    }}
  >
    <Typography variant="h5" align="center" mb={2} mt={1} color="primary">
      {toTitleCase(item.fields?.[0]?.block_label || "N/A")}
    </Typography>
    <Grid container spacing={1}>
      {item.fields.map((field, i) => (
        <Grid key={field.name + i} size={{ xs: 12 }} px={1}>
          <InteractiveField
            field={field}
            onChange={onFieldChange}
            demoVersion={demoVersion}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
));

const DirectFieldCard = memo(({ fields, onFieldChange, demoVersion }) => {
  if (!fields.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "2px solid #d3d3d3",
        borderRadius: "15px",
        px: 2,
        pb: 1,
        boxShadow:
          "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Grid container spacing={1}>
        {fields.map((field, i) => (
          <Grid key={field.name + i} size={{ xs: 12 }} px={1}>
            <InteractiveField
              field={field}
              onChange={onFieldChange}
              demoVersion={demoVersion}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

const PreviewForm = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  fillDemoRef,
  clearRef,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [directItems, setDirectItems] = useState([]);
  const [demoVersion, setDemoVersion] = useState(0);

  const getFormFields = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const fields = res.data.data?.fields;
        setFields(fields);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentSession]);

  useEffect(() => {
    getFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    const fieldArray =
      currentProcessingFile?.document?.tempFields?.formFields ?? [];

    // Sort by page → Y descending (PDF Y=0 is bottom-left) → X ascending
    const sortedFieldArray = [...fieldArray].sort((a, b) => {
      const pageA = a.page_num ?? a.page ?? 0;
      const pageB = b.page_num ?? b.page ?? 0;
      if (pageA !== pageB) return pageA - pageB;

      const yA = a.y ?? a.rect?.Y ?? 0;
      const yB = b.y ?? b.rect?.Y ?? 0;
      if (yB !== yA) return yB - yA; // descending = top of page first

      const xA = a.x ?? a.rect?.X ?? 0;
      const xB = b.x ?? b.rect?.X ?? 0;
      return xA - xB; // ascending = left to right
    });

    const directFields = [];
    const sectionMap = {};

    sortedFieldArray.forEach((field) => {
      const blockId = field.block_id?.split("__sub")?.[0];

      const location = fields?.find(
        (f) => f?.field_id === field?.field_id,
      )?.location;

      if (
        location === "sign" ||
        ["PDFRectangle", "PDFLine"].includes(field.type)
      ) {
        return;
      }

      if (blockId) {
        if (!sectionMap[blockId]) sectionMap[blockId] = [];
        sectionMap[blockId].push({
          ...field,
          label: field?.field_label,
          location,
        });
      } else {
        directFields.push({ ...field, label: field?.field_label, location });
      }
    });

    setSections(
      Object.entries(sectionMap).map(([section, fields]) => ({
        section,
        fields,
      })),
    );
    setDirectItems(directFields);
  }, [
    currentProcessingFile?.document?.tempFields?.formFields,
    fields,
    loading,
  ]);

  const handleFieldChange = useCallback(
    (field, newValue) => {
      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            formFields: prev.document.tempFields.formFields?.map((f) =>
              f.name === field.name ? { ...f, value: newValue } : f,
            ),
          },
        },
      }));
    },
    [setCurrentProcessingFile],
  );

  const handleFillDemo = useCallback(() => {
    setCurrentProcessingFile((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        tempFields: {
          ...prev.document.tempFields,
          formFields: prev.document.tempFields.formFields?.map((f) => {
            if (["PDFRectangle", "PDFLine"].includes(f.type)) return f;

            if (f.type === "PDFCheckBox") return { ...f, value: true };

            if (f.type === "PDFRadioGroup") return { ...f, value: true };

            return {
              ...f,
              value: getDemoValue({ ...f, label: f?.field_label }),
            };
          }),
        },
      },
    }));
    setDemoVersion((v) => v + 1);
  }, [setCurrentProcessingFile]);

  const handleClear = useCallback(() => {
    setCurrentProcessingFile((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        tempFields: {
          ...prev.document.tempFields,
          formFields: prev.document.tempFields.formFields?.map((f) => {
            if (["PDFRectangle", "PDFLine"].includes(f.type)) return f;
            if (f.type === "PDFCheckBox" || f.type === "PDFRadioGroup")
              return { ...f, value: false };
            return { ...f, value: "" };
          }),
        },
      },
    }));
    setDemoVersion((v) => v + 1);
  }, [setCurrentProcessingFile]);

  useEffect(() => {
    if (fillDemoRef) fillDemoRef.current = handleFillDemo;
  }, [fillDemoRef, handleFillDemo]);

  useEffect(() => {
    if (clearRef) clearRef.current = handleClear;
  }, [clearRef, handleClear]);

  const totalFields =
    sections.reduce((acc, s) => acc + s.fields.length, 0) + directItems.length;

  if (loading) {
    return (
      <Box sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 5.5 }}>
        <CircularProgress color="warning" size="4rem" />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} mb={2} mt={2} mx={1}>
      {sections
        .filter((section) => section.fields.length)
        .map((item, i) => (
          <SectionCard
            key={item.section + i}
            item={item}
            onFieldChange={handleFieldChange}
            demoVersion={demoVersion}
          />
        ))}

      <DirectFieldCard
        fields={directItems}
        onFieldChange={handleFieldChange}
        demoVersion={demoVersion}
      />

      {totalFields === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 8,
            gap: 1,
            color: "text.disabled",
          }}
        >
          <Typography variant="h6">No form fields found</Typography>
          <Typography variant="body2">
            Fields will appear here once the PDF is processed.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default PreviewForm;
